import { CompanySelectorController } from '../controller/CompanySelectorController';
import { BaseWebComponentElement } from '../../framework/BaseWebComponentElement';
import { GetAvailableCompaniesParams } from '@nirvana/shared/dtos/types/dashboards/gz-console';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { CompanySelectorEvents } from '../types/constants';
import { SelectOption } from '../../ui/gzDropdown/types';

export class CompanySelectorWebComponent extends BaseWebComponentElement {
  private onCompanyClickedFn?: (clickedCompany: Target) => Promise<void>;
  private onCompanyChangedWithSuccessFn?: () => Promise<void>;
  private onCompanyChangedWithErrorFn?: (error: Error) => Promise<void>;
  public controller!: CompanySelectorController;
  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: CompanySelectorController,
    });
  }

  set onCompanyClicked(onCompanyClickedFn: ((clickedCompany: Target) => Promise<void>) | undefined) {
    this.onCompanyClickedFn = onCompanyClickedFn;
  }

  get onCompanyClicked(): ((clickedCompany: Target) => Promise<void>) | undefined {
    return this.onCompanyClickedFn;
  }

  set onCompanyChangedWithSuccess(onCompanyChangedWithSuccessFn: (() => Promise<void>) | undefined) {
    this.onCompanyChangedWithSuccessFn = onCompanyChangedWithSuccessFn;
  }

  get onCompanyChangedWithSuccess(): (() => Promise<void>) | undefined {
    return this.onCompanyChangedWithSuccessFn;
  }

  set onCompanyChangedWithError(onCompanyChangedWithErrorFn: ((error: Error) => Promise<void>) | undefined) {
    this.onCompanyChangedWithErrorFn = onCompanyChangedWithErrorFn;
  }

  get onCompanyChangedWithError(): ((error: Error) => Promise<void>) | undefined {
    return this.onCompanyChangedWithErrorFn;
  }

  get selectedTarget(): Target {
    const value = this.getAttribute('data-selected-target');
    return value ? JSON.parse(value) : {};
  }

  set selectedTarget(value: Target | undefined) {
    if (!value) {
      this.removeAttribute('data-selected-target');
      return;
    }
    this.setAttribute('data-selected-target', JSON.stringify(value));
  }

  hasAvailableCompanies(): boolean {
    return this.hasAttribute('data-available-companies');
  }

  get availableCompanies(): Array<Target> {
    const values = this.getAttribute('data-available-companies');
    return values ? JSON.parse(values) || [] : [];
  }

  set availableCompanies(value: Array<Target>) {
    if (!value) {
      this.removeAttribute('data-available-companies');
    } else {
      this.setAttribute('data-available-companies', JSON.stringify(value));
      this.dispatchEvent(
        new CustomEvent(CompanySelectorEvents.AVAILABLE_COMPANIES_UPDATED, {
          detail: {
            availableCompanies: value,
            searchString: this.searchString,
          },
        })
      );
    }
  }

  set disabled(value: boolean) {
    this.setAttribute('data-is-disabled', JSON.stringify(value ? true : false));
  }

  get disabled(): boolean {
    const value = this.getAttribute('data-is-disabled');
    return value ? JSON.parse(value) : false;
  }

  set companySelectorParams(value: GetAvailableCompaniesParams) {
    if (!value) {
      return;
    }

    this.setAttribute('data-company-selector-params', JSON.stringify(value));
  }

  get companySelectorParams(): GetAvailableCompaniesParams {
    const value = this.getAttribute('data-company-selector-params');
    return value ? JSON.parse(value) : {};
  }

  public get searchString(): string {
    const value = this.getAttribute('data-search-string');
    return value ?? '';
  }

  public set searchString(value: string) {
    if (!value) {
      return;
    }

    this.setAttribute('data-search-string', value);
  }

  public setSelectedOption(option: SelectOption) {
    this.controller?.view.dom.$companySelectorDropdown.selectOption(option);
  }

  public async selectActiveCompany() {
    await this.controller.selectActiveCompany();
  }

  public set label(label: string) {
    this.setAttribute('label', label);
  }

  public get label(): string {
    const value = this.getAttribute('label');
    return value ?? '';
  }

  public set placeholder(placeholder: string) {
    this.setAttribute('placeholder', placeholder);
  }

  public get placeholder(): string {
    const value = this.getAttribute('placeholder');
    return value ?? '';
  }

  /**
   * Validate that the selected company exists in available companies
   *
   * @param {Array<Target>} value
   * @private
   */
  private validateThatSelectedTargetIsAvailable(value: Array<Target>) {
    // if there are values in availableCompanies then check if the selected company is in availableCompanies
    // if the selected company is not found in availableCompanies then set as target the first value from availableCompanies
    if (value.length) {
      const checkSelectedCompany =
        this.selectedTarget && this.selectedTarget.companyId
          ? value.find(element => element.companyId === this.selectedTarget.companyId)
          : undefined;
      if (!checkSelectedCompany) {
        this.selectedTarget = {
          companyId: value[0].companyId,
          companyName: value[0].companyName,
        };
      }
    } else {
      this.selectedTarget = undefined;
    }
  }
}
